import React, { useState } from "react";
import "./LoginPage.css";

import { useLogin, useNotify, Notification, useRedirect } from "react-admin";
import { ThemeProvider } from "@material-ui/styles";
import {
  InputBase,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Collapse,
  Fade,
} from "@material-ui/core";
import VisibilityRoundedIcon from "@material-ui/icons/VisibilityRounded";
import VisibilityOffRoundedIcon from "@material-ui/icons/VisibilityOffRounded";
import { useMsal } from "@azure/msal-react";

import { loginRequest } from "../../lib/authConfig";
import httpClient from "../../lib/httpClient";
import { userHasMicrosoftAuthPermission } from "../util/permissionsUtils";

const backgroundImageUrl = `${process.env.PUBLIC_URL}images/background/${process.env.REACT_APP_ENVIRONMENT}.jpg`;

export default function LoginPage({ theme }) {
  // Makes sure that on cache-free browser the language selector is set to Italian.
  // Design note: test on cache-free browser.
  const previousLanguage = localStorage.getItem("language");
  if (!previousLanguage) localStorage.setItem("language", "it");

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showLocalSignIn, setShowLocalSignIn] = useState(false);
  const { instance } = useMsal();
  instance
    .handleRedirectPromise()
    .then((s) => {
      if (s !== null && s.account != null) console.log("success");
    })
    .catch((err) => {
      console.log("err", err);
    });

  const login = useLogin();
  const notify = useNotify();
  const redirect = useRedirect();
  const submit = (e) => {
    e && e.preventDefault();

    setLoading(true);
    if (email && password) {
      login({ username: email, password })
        .then((res) => {
          setLoading(false);
        })
        .catch(() => {
          notify("Invalid email or password", { type: "warning" });
          setLoading(false);
        });
    } else {
      notify("Invalid email or password", { type: "warning" });
      setLoading(false);
    }
  };

  const loginWithActiveDirectory = (username, tenantId, accessToken) => {
    setLoading(true);
    httpClient(process.env.REACT_APP_AUTH_URL)
      .post("/auth/activeDirectory", { username, tenantId, accessToken })
      .then((res) => {
        if (res.status === 200) {
          return res.data;
        } else {
          console.log(res?.data?.message);
          notify(res?.data?.message);
          setLoading(false);
          return null;
        }
      })
      .then((user) => {
        localStorage.setItem("user", JSON.stringify(user));
        setLoading(false);
        notify(`Welcome, ${user?.fullName || user?.name || user?.email}`);
        redirect("/");
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);

        notify("You have entered an invalid email or password");
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <div
        className="container"
        style={{
          backgroundImage: `url(${backgroundImageUrl})`,
        }}
      >
        <form className="login" onSubmit={submit}>
          <img
            style={{ height: 100, marginLeft: 10 }}
            src={`${process.env.PUBLIC_URL}images/companies/cystellarLogo.png`}
            alt="Logo"
          />

          <Collapse orientation="vertical" in={showLocalSignIn} mountOnEnter unmountOnExit>
            <div
              style={{
                width: 310,
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              <CYInput
                data-cy="email"
                type="email"
                required
                label="Email"
                placeholder="Enter email"
                value={email}
                setValue={setEmail}
              />
              <CYInput
                data-cy="password"
                type="password"
                label="Password"
                placeholder="Enter password"
                value={password}
                required
                setValue={setPassword}
              />
            </div>
          </Collapse>
          <CYButton
            style={{
              // marginTop: 20,
              width: 310,
              backgroundColor: "#B0BAC9",
            }}
            type="submit"
            loading={loading}
            onClick={() => {
              if (showLocalSignIn) {
                submit();
              } else {
                setShowLocalSignIn((e) => !e);
              }
            }}
          >
            {/* label="Login" */}

            <Fade in={!showLocalSignIn} mountOnEnter unmountOnExit>
              <p style={{ fontWeight: 500, color: "#2E384D" }}>Sign in with Email</p>
            </Fade>
            <Fade in={showLocalSignIn} mountOnEnter unmountOnExit>
              <p style={{ fontWeight: 500, color: "#2E384D" }}>Sign in</p>
            </Fade>
          </CYButton>

          <Divider style={{ width: "100%" }} />

          {userHasMicrosoftAuthPermission() && (
            <CYButton
              type="submit"
              label="Sign in with Active Directory"
              onClick={async () => {
                await instance
                  .loginPopup(loginRequest)
                  .then((res) => {
                    const { username, tenantId } = res?.account;
                    loginWithActiveDirectory(username, tenantId, res.accessToken);
                  })
                  .catch((err) => {
                    console.error("ERR", err);
                  });
              }}
            >
              <img
                style={{ width: 20, height: 20 }}
                src={`${process.env.PUBLIC_URL}images/companies/microsoftWindows11.svg`}
                alt="windowsLogo"
              />
            </CYButton>
          )}
        </form>
        <Notification />
      </div>
    </ThemeProvider>
  );
}

const CYInput = ({ label, placeholder, type, value, required, setValue }) => {
  const [showText, setShowText] = useState(false);
  return (
    <div className="CYInput">
      <h5 className="CYInputLabel">
        {label}:<span> *</span>
      </h5>

      <InputBase
        className="CYInputBase"
        placeholder={placeholder}
        type={type === "password" ? (showText ? "text" : "password") : type || "text"}
        required={true}
        endAdornment={
          type === "password" && (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowText(!showText)}
                onMouseDown={(event) => event.preventDefault()}
                edge="end"
                style={{ marginRight: 0 }}
              >
                {showText ? <VisibilityRoundedIcon /> : <VisibilityOffRoundedIcon />}
              </IconButton>
            </InputAdornment>
          )
        }
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </div>
  );
};

const CYButton = (props) => {
  const { label, loading, style, onClick } = props;

  return (
    <Button
      className="CYButton"
      style={{ backgroundColor: "#2e5bff", ...style }}
      onClick={() => onClick() || {}}
    >
      {loading ? (
        <CircularProgress style={{ color: "#FFF" }} size={16} thickness={7} />
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 10,
          }}
        >
          {props.children}
          <p>{label}</p>
        </div>
      )}
    </Button>
  );
};
